import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../contexts/LanguageContext";
import { SectionTag } from "./About";
import { CONTACT } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t.contact.error);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        lang,
      });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (err?.response?.status === 422 ? t.contact.error : null);
      toast.error(detail || (lang === "ar" ? "فشل الإرسال. حاول لاحقاً." : "Failed to send. Please try again."));
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 sm:py-32 border-t border-white/5"
      data-testid="contact-section"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(0,240,255,0.10), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionTag>{t.contact.tag}</SectionTag>
          <h2
            className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl text-white font-bold leading-[1.1]"
            data-testid="contact-title"
          >
            {t.contact.title}
          </h2>
          <p className="mt-5 text-slate-200 text-base sm:text-lg leading-[1.75]">
            {t.contact.sub}
          </p>

          <div className="mt-10 space-y-5">
            <InfoRow
              icon={<Mail size={16} />}
              label={t.contact.info_email}
              value={CONTACT.email}
              href={`mailto:${CONTACT.email}`}
              testId="contact-email"
              ltr
            />
            <InfoRow
              icon={<Phone size={16} />}
              label={t.contact.info_phone}
              value={CONTACT.phone}
              href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
              testId="contact-phone"
              ltr
            />
            <InfoRow
              icon={<MapPin size={16} />}
              label={t.contact.info_location}
              value={t.contact.location_value}
              testId="contact-location"
            />
          </div>

          <div className="mt-10 pt-8 border-t border-[rgba(0,240,255,0.15)]">
            <p
              className="font-display text-2xl sm:text-3xl text-white"
              data-testid="contact-cta-line"
            >
              <span className="text-[#00F0FF]">›</span> {t.contact.cta_line}
            </p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="lg:col-span-7 border border-[rgba(0,240,255,0.18)] bg-[#0A1428] p-7 sm:p-10"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field
              label={t.contact.name}
              name="name"
              value={form.name}
              onChange={onChange}
              testId="contact-input-name"
            />
            <Field
              label={t.contact.email}
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              testId="contact-input-email"
            />
          </div>
          <div className="mt-5">
            <label className="block font-mono-tech text-[11px] uppercase tracking-[0.24em] text-slate-400 mb-2">
              {t.contact.message}
            </label>
            <textarea
              name="message"
              rows={6}
              value={form.message}
              onChange={onChange}
              data-testid="contact-input-message"
              className="w-full bg-[#050B14] border border-[rgba(0,240,255,0.2)] focus:border-[#00F0FF] focus:outline-none text-white text-sm px-4 py-3 transition-colors placeholder:text-slate-600 resize-none"
              placeholder=""
            />
          </div>
          <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
            <p className="font-mono-tech text-[11px] tracking-[0.24em] uppercase text-slate-500">
              <span className="w-2 h-2 inline-block bg-[#00F0FF] rounded-full me-2 align-middle" />
              SECURE / ENCRYPTED
            </p>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-btn"
              className="inline-flex items-center gap-3 bg-[#00F0FF] text-[#050B14] hover:bg-white transition-colors px-7 py-4 text-xs font-bold uppercase tracking-[0.18em] disabled:opacity-60"
            >
              {sending ? t.contact.sending : t.contact.submit}
              <Send size={16} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, testId }) {
  return (
    <div>
      <label className="block font-mono-tech text-[11px] uppercase tracking-[0.24em] text-slate-400 mb-2">
        {label}
      </label>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full bg-[#050B14] border border-[rgba(0,240,255,0.2)] focus:border-[#00F0FF] focus:outline-none text-white text-sm px-4 py-3 transition-colors"
      />
    </div>
  );
}

function InfoRow({ icon, label, value, href, testId, ltr = false }) {
  const content = (
    <div className="flex items-center gap-4 group">
      <span className="w-10 h-10 inline-flex items-center justify-center border border-[rgba(0,240,255,0.25)] text-[#00F0FF] group-hover:border-[#00F0FF] transition-colors">
        {icon}
      </span>
      <div>
        <div className="font-mono-tech text-[11px] uppercase tracking-[0.24em] text-slate-500">
          {label}
        </div>
        <div
          className={`text-white text-sm sm:text-base mt-0.5 ${ltr ? "wt-ltr" : ""}`}
          dir={ltr ? "ltr" : undefined}
        >
          {value}
        </div>
      </div>
    </div>
  );
  if (href)
    return (
      <a href={href} data-testid={testId}>
        {content}
      </a>
    );
  return <div data-testid={testId}>{content}</div>;
}
